import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

/** Check if the code is running on the client */
export const isClient: boolean = typeof window !== 'undefined';

/** Check if the code is running on the server */
export const isServer: boolean = typeof window === 'undefined';

/** Compares two objects deeply and returns an array of keys that differ */
export const deepCompare = <T>(obj1: T, obj2: T): string[] => {
  const differingKeys: string[] = [];

  const compareValues = (
    keyPrefix: string,
    value1: unknown,
    value2: unknown,
  ) => {
    if (
      typeof value1 === 'object' &&
      typeof value2 === 'object' &&
      value1 !== null &&
      value2 !== null
    ) {
      if (Array.isArray(value1) && Array.isArray(value2)) {
        if (value1.length !== value2.length) {
          differingKeys.push(keyPrefix);
        } else {
          value1.forEach((item, index) => {
            compareValues(index.toString(), item, value2[index]);
          });
        }
      } else {
        // Recursively compare objects
        const allKeys = new Set([
          ...Object.keys(value1),
          ...Object.keys(value2),
        ]);
        allKeys.forEach((key) => {
          compareValues(
            keyPrefix ? `${keyPrefix}.${key}` : key,
            value1[key as keyof typeof value1],
            value2[key as keyof typeof value2],
          );
        });
      }
    } else if (value1 !== value2) {
      differingKeys.push(keyPrefix);
    }
  };

  compareValues('', obj1, obj2);
  return differingKeys
    .map((nestedKey: string) => {
      const key = nestedKey.split('.').pop();
      return key ? key : '';
    })
    .filter((item) => item !== '');
};

export const isNullish = (value: unknown): boolean =>
  value === null || value === undefined;
